import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, Pencil, Plus, Power, Search, Trash2 } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Permit } from '@/components/guards/Permit'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { useListParams } from '@/hooks/useListParams'
import { usePermissions } from '@/hooks/usePermissions'
import { useUsers, useSetUserActive } from '@/hooks/useUsers'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { useAppSelector } from '@/store/hooks'
import { formatDate } from '@/lib/format'
import { avatarBackgroundFor } from '@/lib/avatarBackground'
import { cn } from '@/lib/utils'
import { UserFormDialog, type UserFormMode } from './UserFormDialog'
import { DeleteUserDialog } from './DeleteUserDialog'
import {
  WEB_ROLES,
  USER_STATUSES,
  type PortalUser,
  type WebRole,
} from '@/types'

const ALL = 'all'

const filterTriggerClass = cn(
  'h-10 w-auto shrink-0 justify-between gap-2 rounded-xl border border-[#e7e9ef] bg-white py-3 pl-4 pr-3 text-sm font-normal text-[#171a20] shadow-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:ring-0 [&>span]:line-clamp-none [&>span]:whitespace-nowrap [&_svg]:size-4 [&_svg]:opacity-100',
  FORM_FILTER_HOVER_CLASS
)

const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-left text-[10px] font-medium uppercase tracking-[0.02em] text-[#3d434e]'

const tdClass = 'h-16 border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm'

const actionBtnClass =
  'group/action inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-1.5 text-[#272c36] transition-colors group-hover:bg-[#fbfbfc] disabled:cursor-not-allowed disabled:opacity-50'
const accentActionBtnClass =
  'hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'
const destructiveActionBtnClass =
  'hover:border-[#d63b33] hover:bg-[#fde9e8] hover:text-[#d63b33] group-hover:hover:bg-[#fde9e8]'
const ROLE_TAG: Record<WebRole, { bg: string; text: string }> = {
  Admin: { bg: '#F9F0FF', text: '#531DAB' },
  Manager: { bg: '#DBEAFE', text: '#1D44B3' },
  Operator: { bg: '#FEF3C7', text: '#8A5405' },
}

const COLUMN_COUNT = 7

function RoleTag({ role }: { role: WebRole | null }) {
  const { t } = useTranslation('settings')
  if (!role) return <span className='text-muted-foreground'>—</span>
  const tag = ROLE_TAG[role]
  return (
    <span
      className='inline-flex items-center justify-center rounded-lg px-1.5 py-1 text-xs font-medium leading-4'
      style={{ backgroundColor: tag.bg, color: tag.text }}
    >
      {t(`users.roles.${role}`)}
    </span>
  )
}

function StatusTag({ active }: { active: boolean }) {
  const { t } = useTranslation('settings')
  return (
    <span
      className='inline-flex items-center justify-center rounded-lg px-1.5 py-1 text-xs font-medium leading-4'
      style={
        active
          ? { backgroundColor: '#D1FAE5', color: '#065F46' }
          : { backgroundColor: '#e6e6e6', color: '#1e1e1e' }
      }
    >
      {active ? t('users.statuses.active') : t('users.statuses.inactive')}
    </span>
  )
}

// 2.11.1 - User list. Search + role/status filters, role-aware row actions
// (View / Edit / Deactivate / Delete), hosts Create/Edit/View dialog
// (2.11.2 + 2.11.3) and Delete dialog (2.11.4).
export function UsersPanel() {
  const { t } = useTranslation('settings')
  const { canEdit } = usePermissions()
  const canEditSettings = canEdit('settings')
  const viewerRole = useAppSelector(s => s.auth.user?.role)
  const viewerId = useAppSelector(s => s.auth.user?.id)
  const isAdmin = viewerRole === 'Admin'

  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    setFilters,
    query,
  } = useListParams<{ role?: string[]; status?: string[] }>()
  const { data, isLoading, isError } = useUsers(query)
  const setActive = useSetUserActive()

  const [roleFilter, setRoleFilter] = useState<string>(ALL)
  const [statusFilter, setStatusFilter] = useState<string>(ALL)
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<UserFormMode>('create')
  const [returnToViewOnCancel, setReturnToViewOnCancel] = useState(false)
  const [selected, setSelected] = useState<PortalUser | null>(null)
  const [deleting, setDeleting] = useState<PortalUser | null>(null)

  const rows = useMemo(() => data?.data ?? [], [data])

  const activeAdminCount = useMemo(
    () => rows.filter(u => u.role === 'Admin' && u.is_active).length,
    [rows]
  )

  const filterRoles = useMemo(
    () => (isAdmin ? WEB_ROLES : WEB_ROLES.filter(r => r !== 'Admin')),
    [isAdmin]
  )

  function applyRole(value: string) {
    setRoleFilter(value)
    setFilters({ role: value === ALL ? undefined : [value] })
  }
  function applyStatus(value: string) {
    setStatusFilter(value)
    setFilters({ status: value === ALL ? undefined : [value] })
  }

  function openCreate() {
    setReturnToViewOnCancel(false)
    setSelected(null)
    setFormMode('create')
    setFormOpen(true)
  }
  function openView(u: PortalUser) {
    setReturnToViewOnCancel(false)
    setSelected(u)
    setFormMode('view')
    setFormOpen(true)
  }
  function openEdit(u: PortalUser) {
    setReturnToViewOnCancel(false)
    setSelected(u)
    setFormMode('edit')
    setFormOpen(true)
  }
  function switchToEdit() {
    setReturnToViewOnCancel(true)
    setFormMode('edit')
  }

  function handleFormOpenChange(open: boolean) {
    setFormOpen(open)
    if (!open) setReturnToViewOnCancel(false)
  }

  function handleFormCancel() {
    if (formMode === 'edit' && returnToViewOnCancel && selected) {
      setReturnToViewOnCancel(false)
      setFormMode('view')
      return
    }

    handleFormOpenChange(false)
  }

  function toggleActive(u: PortalUser) {
    setActive.mutate(
      { id: u.id, isActive: !u.is_active },
      {
        onSuccess: () =>
          toast({
            variant: 'success',
            title: u.is_active
              ? t('users.toast.deactivated')
              : t('users.toast.activated'),
          }),
        onError: e =>
          toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
      }
    )
  }

  // Manager cannot View/Edit/Disable/Delete Admin rows (WBS).
  function canManageRow(u: PortalUser) {
    if (isAdmin) return true
    return u.role !== 'Admin'
  }

  function isLastActiveAdmin(u: PortalUser) {
    return u.role === 'Admin' && u.is_active && activeAdminCount <= 1
  }

  return (
    <section className='rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-12px_rgba(38,45,66,0.1)]'>
      <div className='flex flex-col gap-6 py-6'>
        <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
          <div className='min-w-0 flex-1'>
            <div className='flex min-w-0 items-center border-l-4 border-[#ff5a1f] px-6 py-1'>
              <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
                {t('users.title')}
              </h2>
            </div>
          </div>
          <Permit module='settings' action='edit'>
            <Button
              className='mx-4 h-auto rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] p-3 text-sm font-medium leading-1 text-white shadow-none hover:bg-[#e6511c] sm:mx-5 lg:mx-6'
              onClick={openCreate}
            >
              <Plus className='mr-2 h-4 w-4' />
              {t('users.addUser')}
            </Button>
          </Permit>
        </div>

        <div className='flex flex-col gap-4 px-4 sm:px-5 lg:px-6 lg:flex-row lg:items-center'>
          <div className='relative w-full max-w-[320px] shrink-0'>
            <Search className='pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6c7285]' />
            <Input
              name='tf-users-list-search'
              autoComplete='one-time-code'
              value={search}
              maxLength={100}
              onChange={e => onSearchChange(e.target.value)}
              placeholder={t('users.searchPlaceholder')}
              className={cn(
                'h-10 rounded-xl border border-[#e7e9ef] bg-white pl-11 pr-4 text-sm font-normal text-[#171a20] shadow-none placeholder:text-[#6c7285]',
                FORM_CONTROL_FOCUS_CLASS,
                FORM_FILTER_HOVER_CLASS
              )}
            />
          </div>

          <div className='flex min-w-0 flex-wrap items-center gap-3'>
            <Select value={roleFilter} onValueChange={applyRole}>
              <SelectTrigger
                className={cn(filterTriggerClass, 'min-w-[115px]')}
              >
                <SelectValue placeholder={t('users.filters.role')}>
                  {roleFilter === ALL
                    ? t('users.filters.role')
                    : t(`users.roles.${roleFilter}`)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>
                  {t('users.filters.allRoles')}
                </SelectItem>
                {filterRoles.map(r => (
                  <SelectItem key={r} value={r}>
                    {t(`users.roles.${r}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={applyStatus}>
              <SelectTrigger
                className={cn(filterTriggerClass, 'min-w-[143px]')}
              >
                <SelectValue placeholder={t('users.filters.status')}>
                  {statusFilter === ALL
                    ? t('users.filters.status')
                    : t(`users.statuses.${statusFilter}`)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>
                  {t('users.filters.allStatuses')}
                </SelectItem>
                {USER_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>
                    {t(`users.statuses.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className='px-4 pb-2 sm:px-5'>
        <div className='overflow-x-auto'>
          <table className='w-full min-w-[920px] caption-bottom text-sm'>
            <TableHeader>
              <TableRow className='border-0'>
                <TableHead className={thClass}>
                  {t('users.columns.name')}
                </TableHead>
                <TableHead className={thClass}>
                  {t('users.columns.email')}
                </TableHead>
                <TableHead className={cn(thClass, 'text-center')}>
                  {t('users.columns.role')}
                </TableHead>
                <TableHead className={thClass}>
                  {t('users.columns.title')}
                </TableHead>
                <TableHead className={cn(thClass, 'text-center')}>
                  {t('users.columns.status')}
                </TableHead>
                <TableHead className={thClass}>
                  {t('users.columns.lastLogin')}
                </TableHead>
                <TableHead className={cn(thClass, 'w-[148px]')}>
                  <div className='ml-auto text-right'>
                    {t('users.columns.actions')}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                      <Skeleton className='h-6 w-full' />
                    </TableCell>
                  </TableRow>
                ))}

              {isError && !isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      tdClass,
                      'py-10 text-center text-destructive'
                    )}
                  >
                    {t('users.loadError')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !isError && rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      tdClass,
                      'py-10 text-center text-muted-foreground'
                    )}
                  >
                    {t('users.empty')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                !isError &&
                rows.map(u => {
                  const manageable = canManageRow(u)
                  const isSelf = u.id === viewerId
                  const lastAdmin = isLastActiveAdmin(u)
                  const avatarName = u.full_name ?? u.email ?? u.id
                  return (
                    <TableRow
                      key={u.id}
                      className={cn(
                        'group border-0',
                        manageable && 'cursor-pointer'
                      )}
                      onClick={event => {
                        if (!manageable || !(event.target instanceof Element))
                          return

                        if (
                          event.target.closest(
                            'button, a, input, select, textarea, [role="button"]'
                          )
                        )
                          return

                        openView(u)
                      }}
                    >
                      <TableCell
                        className={cn(
                          tdClass,
                          'relative pl-4 before:absolute before:bottom-0 before:left-0 before:top-0 before:w-0.5 before:bg-[#ff5a1f] before:opacity-0 before:transition-opacity group-hover:before:opacity-100'
                        )}
                      >
                        <div className='flex items-center gap-3'>
                          <Avatar
                            name={u.full_name}
                            src={u.avatar}
                            className='size-8 border border-white bg-transparent font-heading text-sm font-semibold text-white'
                            style={{
                              background: avatarBackgroundFor(avatarName),
                            }}
                          />
                          {manageable ? (
                            <button
                              type='button'
                              className='text-left text-[14px] font-semibold leading-[1.4] text-[#171a20] transition-colors group-hover:text-[#ff5a1f] hover:underline'
                              onClick={() => openView(u)}
                            >
                              {u.full_name ?? '—'}
                            </button>
                          ) : (
                            <span className='text-[14px] font-semibold leading-[1.4] text-[#171a20]'>
                              {u.full_name ?? '—'}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={cn(tdClass)}>
                        <span className='text-sm leading-[1.4] text-[#171a20]'>
                          {u.email ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, 'text-center')}>
                        <RoleTag role={u.role} />
                      </TableCell>
                      <TableCell className={cn(tdClass)}>
                        <span className='text-sm leading-[1.4] text-[#171a20]'>
                          {u.title ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, 'text-center')}>
                        <StatusTag active={u.is_active} />
                      </TableCell>
                      <TableCell className={cn(tdClass)}>
                        <span className='whitespace-nowrap text-sm leading-[1.4] text-[#171a20]'>
                          {formatDate(u.last_login_at)}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass)}>
                        <div className='flex items-center justify-end gap-2'>
                          {manageable && (
                            <>
                              <TruncatedTextTooltip
                                content={t('users.actions.view')}
                                delayDuration={0}
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                              >
                                <button
                                  type='button'
                                  className={cn(
                                    actionBtnClass,
                                    accentActionBtnClass
                                  )}
                                  aria-label={t('users.actions.view')}
                                  onClick={() => openView(u)}
                                >
                                  <Eye className='size-4' />
                                </button>
                              </TruncatedTextTooltip>
                              {canEditSettings && !isSelf && (
                                <TruncatedTextTooltip
                                  content={
                                    u.is_active
                                      ? t('users.actions.deactivate')
                                      : t('users.actions.activate')
                                  }
                                  delayDuration={0}
                                  alwaysShow
                                  closeOnPointerDown
                                  className='inline-flex shrink-0'
                                >
                                  <button
                                    type='button'
                                    className={cn(
                                      actionBtnClass,
                                      accentActionBtnClass
                                    )}
                                    aria-label={
                                      u.is_active
                                        ? t('users.actions.deactivate')
                                        : t('users.actions.activate')
                                    }
                                    disabled={
                                      setActive.isPending ||
                                      (lastAdmin && u.is_active)
                                    }
                                    onClick={() => toggleActive(u)}
                                  >
                                    <Power
                                      className={cn(
                                        'size-4',
                                        !u.is_active &&
                                          'text-[#8f95a6] group-hover/action:text-[#ff5a1f]'
                                      )}
                                    />
                                  </button>
                                </TruncatedTextTooltip>
                              )}
                              {canEditSettings && (
                                <TruncatedTextTooltip
                                  content={t('common:actions.edit')}
                                  delayDuration={0}
                                  alwaysShow
                                  closeOnPointerDown
                                  className='inline-flex shrink-0'
                                >
                                  <button
                                    type='button'
                                    className={cn(
                                      actionBtnClass,
                                      accentActionBtnClass
                                    )}
                                    aria-label={t('common:actions.edit')}
                                    onClick={() => openEdit(u)}
                                  >
                                    <Pencil className='size-4' />
                                  </button>
                                </TruncatedTextTooltip>
                              )}
                              {isAdmin && !isSelf && !lastAdmin && (
                                <Permit module='settings' action='delete'>
                                  <TruncatedTextTooltip
                                    content={t('common:actions.delete')}
                                    delayDuration={0}
                                    alwaysShow
                                    closeOnPointerDown
                                    className='inline-flex shrink-0'
                                  >
                                    <button
                                      type='button'
                                      className={cn(
                                        actionBtnClass,
                                        destructiveActionBtnClass
                                      )}
                                      aria-label={t('common:actions.delete')}
                                      onClick={() => setDeleting(u)}
                                    >
                                      <Trash2 className='size-4' />
                                    </button>
                                  </TruncatedTextTooltip>
                                </Permit>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
            </TableBody>
          </table>
        </div>
      </div>

      <ListPagination
        metadata={data?.metadata}
        page={page}
        pageSize={limit}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
        className='px-4 sm:px-5'
        showShowingRange={false}
      />

      <UserFormDialog
        open={formOpen}
        onOpenChange={handleFormOpenChange}
        user={selected}
        mode={formMode}
        viewerRole={viewerRole}
        canEdit={!!selected && canManageRow(selected) && canEditSettings}
        protectLastAdmin={!!selected && isLastActiveAdmin(selected)}
        onRequestEdit={switchToEdit}
        onCancel={handleFormCancel}
      />
      <DeleteUserDialog
        open={!!deleting}
        onOpenChange={o => !o && setDeleting(null)}
        user={deleting}
      />
    </section>
  )
}

export default UsersPanel
